import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";
import { Archive as ArchiveIcon } from "@mui/icons-material";
import { api } from "../services/api";
import { formatDate } from "../utils/dateFormat";

interface ArchivedArticle {
  id: string;
  title: string;
  subtitle?: string;
  authorId: string;
  status: string;
  publishedAt?: any;
  archivedAt: any;
  archivedBy: string;
  originalId: string;
}

const ArchivedArticles: React.FC = () => {
  const [articles, setArticles] = useState<ArchivedArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchArchivedArticles();
  }, []);

  const fetchArchivedArticles = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await api.getArchivedNews();

      if (result.success && result.articles) {
        setArticles(result.articles);
      } else {
        setError(result.error || "Failed to fetch archived articles");
      }
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box m={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box m={3}>
      <Box display="flex" alignItems="center" mb={3}>
        <ArchiveIcon sx={{ mr: 1, fontSize: 32 }} />
        <Typography variant="h4">Archived Articles</Typography>
      </Box>

      {articles.length === 0 ? (
        <Alert severity="info">No archived articles found.</Alert>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Showing {articles.length} archived article{articles.length !== 1 ? 's' : ''}
          </Typography>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Title</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Original Published</strong></TableCell>
                  <TableCell><strong>Archived Date</strong></TableCell>
                  <TableCell><strong>Archived By</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {articles.map((article) => (
                  <TableRow key={article.id} hover>
                    <TableCell>
                      <Typography variant="body1" fontWeight="medium">
                        {article.title}
                      </Typography>
                      {article.subtitle && (
                        <Typography variant="body2" color="text.secondary">
                          {article.subtitle.substring(0, 80)}
                          {article.subtitle.length > 80 ? "..." : ""}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={article.status}
                        size="small"
                        color={
                          article.status === "published"
                            ? "success"
                            : article.status === "approved"
                            ? "primary"
                            : "default"
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {article.publishedAt
                        ? formatDate(article.publishedAt)
                        : "Not published"}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(article.archivedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {article.archivedBy.substring(0, 8)}...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default ArchivedArticles;
